/**
 * Territory splitter — divides a lead set into N driver routes by a chosen
 * grouping axis. Two axes today; the design is intentionally extensible to
 * more (city, state, route template) by plugging in another `groupLeadsBy`
 * variant.
 *
 * Modes:
 *   - "compass" — 8 fixed sectors radiating from the depot (N, NE, E, SE,
 *     S, SW, W, NW). Sector boundaries are static relative to true north,
 *     so they don't reshape when the lead set changes — adding a lead just
 *     buckets it; the other 7 sectors are unaffected. Best for "blanket the
 *     market from a single depot."
 *
 *   - "zip" — bucket by lead.address_zip, distribute ZIPs to drivers in
 *     numerical-ascending order. Each driver gets a contiguous slice of
 *     ZIPs. Best for "everyone working one city/county, divide by ZIP."
 *
 * The user explicitly asked us NOT to balance lead counts. A sector with 80
 * leads goes to one driver; a sector with 4 to another. Their argument was
 * "simplicity is genius" — admins know their team and can adjust by picking
 * which drivers get which slices on a future run. The algorithm just
 * deterministically applies the rule.
 */

import { optimizeRoute } from "@/lib/distance";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SplitMode = "compass" | "zip";

/** The minimum lead shape the splitter needs. Real callers pass MappableLead
 *  or LeadInZone — both are structurally compatible. */
export interface SplittableLead {
  id: string;
  lat: number | null;
  lng: number | null;
  address_zip?: string | null;
}

export interface LatLng {
  lat: number;
  lng: number;
}

/** Output of the splitter: one entry per driver, plus any leads dropped
 *  because they had no usable group key (e.g. zip mode with a lead missing
 *  a ZIP — they're surfaced so the admin can fix the data and re-run). */
export interface SplitAssignment {
  driverId: string;
  driverName: string;
  /** The bucket labels this driver owns ("NE,E,SE" or "44053,44054"). */
  groupLabels: string[];
  /** Leads in those buckets, in original input order. */
  leads: SplittableLead[];
  /** 2-opt-optimized order of stop ids — drop into createRoute as-is.
   *  `legs` are haversine; the real driving order is locked in for save. */
  orderedLeadIds: string[];
  totalMeters: number;
}

export interface SplitResult {
  assignments: SplitAssignment[];
  /** Leads with no usable group key (e.g. ungeocoded + compass mode; no ZIP
   *  + zip mode). Excluded from any route. */
  unassigned: SplittableLead[];
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export interface SplitInput {
  mode: SplitMode;
  origin: LatLng; // start depot
  end: LatLng | null; // null = open path; same as origin = round-trip
  leads: SplittableLead[];
  drivers: Array<{ id: string; name: string }>;
}

export function splitTerritory(input: SplitInput): SplitResult {
  const { mode, origin, end, leads, drivers } = input;
  if (drivers.length === 0) {
    return { assignments: [], unassigned: leads };
  }

  // 1. Bucket leads by the chosen grouping axis. Returns Map<bucketKey, leads[]>
  //    plus the "natural order" of bucket keys (clockwise for compass,
  //    numerical ascending for ZIP) — drivers will receive a contiguous slice
  //    of this ordered list.
  const { buckets, orderedKeys, unassigned } =
    mode === "compass" ? bucketByCompass(leads, origin) : bucketByZip(leads);

  // 2. Distribute buckets across drivers. Each driver gets ceil(B/N) or
  //    floor(B/N) consecutive keys — front-loaded, so the first few drivers
  //    take the slightly-larger share when buckets don't divide evenly.
  const driverBuckets = distributeKeysToDrivers(orderedKeys, drivers.length);

  // 3. For each driver, collect their bucket leads and 2-opt-optimize the
  //    visit order from `origin` → leads → `end`. Output is the ordered
  //    lead id list ready for the createRoute stops field.
  const assignments: SplitAssignment[] = drivers.map((driver, i) => {
    const myKeys = driverBuckets[i] ?? [];
    const myLeads: SplittableLead[] = [];
    for (const k of myKeys) {
      const bucket = buckets.get(k);
      if (bucket) myLeads.push(...bucket);
    }
    // optimizeRoute needs lat/lng on every point — leads without coords were
    // already pulled out into `unassigned` upstream for compass mode; in zip
    // mode some leads may still lack coords (zip-only matching). Filter again.
    const routable = myLeads.filter(
      (l): l is SplittableLead & { lat: number; lng: number } =>
        l.lat != null && l.lng != null,
    );
    if (routable.length === 0) {
      return {
        driverId: driver.id,
        driverName: driver.name,
        groupLabels: myKeys,
        leads: myLeads,
        orderedLeadIds: [],
        totalMeters: 0,
      };
    }
    const optimized = optimizeRoute(
      origin,
      routable.map((l) => ({ id: l.id, lat: l.lat, lng: l.lng })),
      end,
    );
    return {
      driverId: driver.id,
      driverName: driver.name,
      groupLabels: myKeys,
      leads: myLeads,
      orderedLeadIds: optimized.ordered.map((p) => p.id),
      totalMeters: optimized.totalMeters,
    };
  });

  return { assignments, unassigned };
}

// ---------------------------------------------------------------------------
// Compass bucketing (8 sectors, fixed 45° each, aligned to true north)
// ---------------------------------------------------------------------------

// Internal — exported earlier for unit tests we didn't end up writing.
// De-exported to keep the module surface honest; re-export when a real
// consumer surfaces.
const COMPASS_SECTORS = [
  "N",
  "NE",
  "E",
  "SE",
  "S",
  "SW",
  "W",
  "NW",
] as const;
type CompassSector = (typeof COMPASS_SECTORS)[number];

/**
 * Compass sector of a lead relative to a depot. Uses bearing (azimuth) from
 * depot to lead — angle measured clockwise from true north.
 *
 * Sector boundaries (degrees, half-open intervals, modulo 360):
 *   N   :  337.5 ≤ θ <  22.5   (wraps around 360°)
 *   NE  :   22.5 ≤ θ <  67.5
 *   E   :   67.5 ≤ θ < 112.5
 *   SE  :  112.5 ≤ θ < 157.5
 *   S   :  157.5 ≤ θ < 202.5
 *   SW  :  202.5 ≤ θ < 247.5
 *   W   :  247.5 ≤ θ < 292.5
 *   NW  :  292.5 ≤ θ < 337.5
 */
function compassSectorOf(lead: LatLng, depot: LatLng): CompassSector {
  const bearing = bearingDeg(depot, lead);
  // Shift by 22.5 so N's interval becomes [0, 45) without wrap math.
  const shifted = (bearing + 22.5 + 360) % 360;
  const idx = Math.floor(shifted / 45);
  return COMPASS_SECTORS[idx];
}

/** Bearing in degrees clockwise from true north. Pure haversine; for sector
 *  classification at typical depot-to-lead distances (sub-50km), the
 *  flat-earth approximation is plenty accurate. */
function bearingDeg(from: LatLng, to: LatLng): number {
  const φ1 = (from.lat * Math.PI) / 180;
  const φ2 = (to.lat * Math.PI) / 180;
  const Δλ = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
}

function bucketByCompass(
  leads: SplittableLead[],
  depot: LatLng,
): { buckets: Map<string, SplittableLead[]>; orderedKeys: string[]; unassigned: SplittableLead[] } {
  const buckets = new Map<string, SplittableLead[]>(
    COMPASS_SECTORS.map((s) => [s, [] as SplittableLead[]]),
  );
  const unassigned: SplittableLead[] = [];
  for (const lead of leads) {
    if (lead.lat == null || lead.lng == null) {
      unassigned.push(lead);
      continue;
    }
    // Skip the exact-same-point case to avoid NaN bearing.
    if (lead.lat === depot.lat && lead.lng === depot.lng) {
      unassigned.push(lead);
      continue;
    }
    const sector = compassSectorOf({ lat: lead.lat, lng: lead.lng }, depot);
    buckets.get(sector)!.push(lead);
  }
  // Return the ordered keys in compass clockwise order — drivers get
  // contiguous slices of this list. Drop empty sectors so a driver doesn't
  // end up with a phantom "NW (empty)" slice when no leads fell there.
  const orderedKeys = COMPASS_SECTORS.filter((s) => (buckets.get(s) ?? []).length > 0);
  return { buckets, orderedKeys: [...orderedKeys], unassigned };
}

// ---------------------------------------------------------------------------
// ZIP bucketing (one bucket per distinct ZIP, sorted ascending)
// ---------------------------------------------------------------------------

function bucketByZip(
  leads: SplittableLead[],
): { buckets: Map<string, SplittableLead[]>; orderedKeys: string[]; unassigned: SplittableLead[] } {
  const buckets = new Map<string, SplittableLead[]>();
  const unassigned: SplittableLead[] = [];
  for (const lead of leads) {
    const zip = (lead.address_zip ?? "").trim();
    if (!zip) {
      unassigned.push(lead);
      continue;
    }
    if (!buckets.has(zip)) buckets.set(zip, []);
    buckets.get(zip)!.push(lead);
  }
  // Natural ZIP order — ascending lexicographic which == numerical for
  // 5-digit US ZIPs. Drivers get a contiguous range of ZIPs.
  const orderedKeys = [...buckets.keys()].sort();
  return { buckets, orderedKeys, unassigned };
}

// ---------------------------------------------------------------------------
// Distribution: chunk N ordered keys across K drivers
// ---------------------------------------------------------------------------

/**
 * Splits B ordered bucket keys across N drivers. Front-loads the remainder:
 * if buckets don't divide evenly, the first (B mod N) drivers each get one
 * extra bucket. Output is array-of-arrays — drivers[i] is the list of bucket
 * keys assigned to driver i.
 *
 * Examples (B=buckets, N=drivers):
 *   B=8, N=4 → [2, 2, 2, 2] (each driver gets 2 sectors)
 *   B=8, N=5 → [2, 2, 2, 1, 1]
 *   B=8, N=8 → [1, 1, 1, 1, 1, 1, 1, 1]
 *   B=8, N=1 → [8]
 *   B=8, N=12 → [1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0]  ← over-supply: trailing
 *               drivers get empty assignments. UI should warn the admin and
 *               filter empty assignments before saving.
 */
function distributeKeysToDrivers(
  orderedKeys: string[],
  driverCount: number,
): string[][] {
  if (driverCount <= 0) return [];
  const B = orderedKeys.length;
  const baseSize = Math.floor(B / driverCount);
  const extras = B % driverCount;
  const out: string[][] = [];
  let cursor = 0;
  for (let i = 0; i < driverCount; i++) {
    const take = baseSize + (i < extras ? 1 : 0);
    out.push(orderedKeys.slice(cursor, cursor + take));
    cursor += take;
  }
  return out;
}
